/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import memoize from 'lodash/memoize';
import { Box, xcss, Text } from '@atlaskit/primitives';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import Anchor from '@atlaskit/primitives/anchor';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import {
	HoverPopoverWithContext,
	CellHoverRenderer,
} from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsSingleLineRowHeightEnabled } from '../../../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

/**
 * We memoize this event by the **first** argument, e.g. fieldType, as we only really care about the proportion of
 * users encountering this scenario. We don't need to send an event each time this component is rendered.
 */
export const fireTrackAnalyticsOncePerFieldType = memoize(
	(fieldType: string, numberOfElements: number, createAnalyticsEvent) => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'tableCell multipleElementsRendered', {
			fieldType,
			numberOfElements,
		});
	},
);

type Props = {
	/**
	 * Table cell element to render for an issue field.
	 */
	cellElements: ReactNode[];
	/**
	 * Flag to enable min-height containers around each individual cell element as a stop-gap solution to fix layout
	 * issues with duplicate inline editable fields (which have built-in `min-height: 100%`).
	 * TODO JSC-1783 Handle duplicate field layouts within single row height
	 */
	multipleFieldLayoutAdjustment?: boolean;
	/**
	 * Type of the issue field.
	 */
	type: string;
	/**
	 * issueKey of the issue row where cell is rendered.
	 */
	issueKey?: string;
	/**
	 * column name of the issue field.
	 */
	fieldName?: string;
};

const MoreFieldsContent = ({ elements }: { elements: ReactNode[] }) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{/* Row */}
			<Box xcss={moreFieldsHeaderRowStyles}>
				<Text size="medium" weight="medium">
					{formatMessage(messages.duplicateFieldsHeader)}
				</Text>
			</Box>
			{/* Body */}
			<Box xcss={moreFieldsDescriptionStyles}>
				{formatMessage(messages.duplicateFieldsContent, {
					link: (chunks: ReactNode) => (
						<Anchor href="/jira/settings/issues/fields" target="_blank">
							{chunks}
						</Anchor>
					),
				})}
			</Box>
			<hr role="presentation" />
			<Box xcss={fieldsContainerStyles}>
				{elements.map((element, idx) => (
					<Box xcss={fieldStyles} key={idx}>
						{element}
					</Box>
				))}
			</Box>
		</Box>
	);
};

export const MultipleCellElements = ({
	issueKey,
	cellElements,
	multipleFieldLayoutAdjustment = false,
	type,
	fieldName,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	fireTrackAnalyticsOncePerFieldType(type, cellElements.length, createAnalyticsEvent);

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const { formatMessage } = useIntl();

	if (multipleFieldLayoutAdjustment && cellElements.length > 0 && isSingleLineRowHeightEnabled) {
		const firstElement = cellElements[0];
		const rest = cellElements.slice(1);

		return (
			<Box xcss={rowStyle}>
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<div css={[contentStyles, fillHeight, fillWidth]} {...props}>
								{firstElement}
							</div>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<HoverPopoverWithContext<HTMLDivElement>
								content={<MoreFieldsContent elements={rest} />}
								isInteractive
								isFixedWidth
								analyticsAttributes={{
									duplicateField: true,
								}}
							>
								{({ ref }) => (
									<div css={[buttonStyle]} ref={ref}>
										<IconButton
											label={
												fg('nin_duplicated_fields_info_a11y_fix')
													? formatMessage(messages.duplicateFieldsInfoIconMessage, {
															issueKey: issueKey ?? '',
															fieldName: fieldName ?? '',
														})
													: ''
											}
											appearance="subtle"
											icon={(iconProps) => (
												<StatusInformationIcon
													{...iconProps}
													color={token('color.icon.information')}
												/>
											)}
											testId="native-issue-table.ui.row.issue-row.cell.multiple-cell-elements.issue-table"
											{...props}
										/>
									</div>
								)}
							</HoverPopoverWithContext>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
			</Box>
		);
	}

	return multipleFieldLayoutAdjustment ? (
		<Box xcss={containerStyles}>
			{cellElements.map((cellElement, index) => (
				<Box key={index} xcss={elementContainerStyles}>
					{cellElement}
				</Box>
			))}
		</Box>
	) : (
		cellElements
	);
};

const rowStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 'space.050',
	height: '100%',
});

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	gap: 'space.050',
});

const elementContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: '32px',
});

const moreFieldsHeaderRowStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginBottom: 'space.050',
});

const moreFieldsDescriptionStyles = xcss({
	font: 'font.body.medium',
});

const fieldsContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	gap: 'space.050',
	width: '100%',
	height: '100%',
});

const fieldStyles = xcss({
	height: '40px',
	width: '100%',
});

const contentStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const buttonStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginRight: token('space.050'),
});

const fillHeight = css({
	height: '100%',
});

const fillWidth = css({
	width: '100%',
});
